import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CoverNoteModal } from '../../components/coverNote/CoverNoteModal';
import { DataTable, type Column } from '../../components/table/DataTable';
import { EMPTY_CELL, type CaseSubmissionRequest } from '../../data/mailRoom';
import { fetchMailroomColumnSuggestions, listMailroomCaseRequests, SORT_COL_MAP } from '../../services/mailRoom';
import { onHoldRowClass } from '../../utils/taskRowHighlight';

function isEmptyCell(value: string): boolean {
  return !value.trim() || value === EMPTY_CELL;
}

export default function MailRoomRequestsPage() {
  const { t } = useTranslation();

  return (
    <div style={{ padding: 0 }}>
      <div className="bc">
        <span>{t('mailRoom.breadcrumb')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('mailRoom.title')}</div>
          {/* <div className="pg-sub">View a list of all case submission requests.</div> */}
        </div>
      </div>

      <MailRoomRequestsTable />
    </div>
  );
}

function MailRoomRequestsTable() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CaseSubmissionRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverRow, setCoverRow] = useState<CaseSubmissionRequest | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'ref', dir: 'asc' });

  const fetchAbortRef = useRef<AbortController | null>(null);
  const filtersRef = useRef<Record<string, string>>({});

  const fetchList = useCallback(
    (p: number, size: number, filters: Record<string, string>, sortKey: string, sortDir: 'asc' | 'desc') => {
      fetchAbortRef.current?.abort();
      fetchAbortRef.current = new AbortController();
      const { signal } = fetchAbortRef.current;
      setLoading(true);
      setError(null);

      void listMailroomCaseRequests({
        page: p - 1,
        pageSize: size,
        columnFilters: filters,
        sortBy: SORT_COL_MAP[sortKey] ?? 'fileRefNo',
        sortDir: sortDir === 'asc' ? 'ASC' : 'DESC',
        signal,
      })
        .then((result) => {
          if (signal.aborted) return;
          setRows(result.items);
          setTotalCount(result.total);
          setTotalPages(result.totalPages);
        })
        .catch(() => {
          if (signal.aborted) return;
          setError(t('mailRoom.loadFailed'));
          setRows([]);
          setTotalCount(0);
          setTotalPages(1);
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [t],
  );

  const fetchRef = useRef(fetchList);
  fetchRef.current = fetchList;

  useEffect(() => {
    fetchRef.current(page, pageSize, filtersRef.current, sort.key, sort.dir);
    return () => fetchAbortRef.current?.abort();
  }, [page, pageSize, sort.key, sort.dir]);

  const handleFiltersChange = useCallback(
    (colFilters: Record<string, string>) => {
      filtersRef.current = colFilters;
      setPage(1);
      fetchRef.current(1, pageSize, colFilters, sort.key, sort.dir);
    },
    [pageSize, sort.key, sort.dir],
  );

  const handleSortChange = useCallback(
    (key: string, dir: 'asc' | 'desc') => {
      setSort({ key, dir });
      setPage(1);
    },
    [],
  );

  const columns = useMemo<Column<CaseSubmissionRequest>[]>(() => {
    return [
      {
        key: 'ref',
        header: t('tasks.shared.fileRefNo'),
        getValue: (r) => r.ref,
        filterPattern: /^[a-zA-Z0-9/._ -]*$/,
        render: (r) => <span className="rn">{r.ref}</span>,
      },
      { key: 'fileCategory', header: t('tasks.shared.fileCategory'), getValue: (r) => r.fileCategory },
      {
        key: 'subFileCategory',
        header: t('tasks.shared.subFileCategory'),
        getValue: (r) => r.subFileCategory,
        render: (r) => (
          <span style={{ color: isEmptyCell(r.subFileCategory) ? 'var(--t3)' : undefined }}>{r.subFileCategory}</span>
        ),
      },
      { key: 'agency', header: t('tasks.shared.submittedAgency'), getValue: (r) => r.agency },
      {
        key: 'fs',
        header: t('mailRoom.physicalFileStatus'),
        getValue: (r) => r.statusLabel,
        render: (r) => <span className={`pill ${r.statusClass}`}>{r.statusLabel}</span>,
      },
      { key: 'delivery', header: t('mailRoom.fileDeliveryMethod'), getValue: (r) => r.deliveryMethod },
      {
        key: 'movementAt',
        header: t('mailRoom.fileMovementDateTime'),
        getValue: (r) => r.movementAt,
        filterType: 'date-range',
        render: (r) => (
          <span style={{ fontSize: 12, color: isEmptyCell(r.movementAt) ? 'var(--t3)' : undefined }}>{r.movementAt}</span>
        ),
      },
      {
        key: 'custody',
        header: t('mailRoom.physicalFileCustody'),
        getValue: (r) => r.custody,
        render: (r) => (
          <span style={{ color: isEmptyCell(r.custody) ? 'var(--t3)' : 'var(--t2)' }}>{r.custody}</span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            <button
              className="act-b"
              type="button"
              data-tip={t('mailRoom.viewCoverNote')}
              aria-label={t('mailRoom.viewCoverNote')}
              onClick={() => setCoverRow(r)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          </div>
        ),
      },
    ];
  }, [t]);

  return (
    <>
      {error ? <div className="si-err" style={{ marginBottom: 12 }}>{error}</div> : null}

      <DataTable
        title={t('mailRoom.tableTitle')}
        columns={columns}
        rows={rows}
        rowKey={(r) => r.caseRequestId ?? r.ref}
        rowClassName={(r) => onHoldRowClass(r.statusLabel)}
        defaultRowsPerPage={10}
        rowsPerPageOptions={[5, 10, 15, 20]}
        previousLabel={t('tasks.shared.previousBack')}
        nextLabel={t('tasks.shared.nextForward')}
        loading={loading}
        fetchColumnSuggestions={fetchMailroomColumnSuggestions}
        onFiltersChange={handleFiltersChange}
        serverSort={{
          key: sort.key,
          dir: sort.dir,
          onSortChange: handleSortChange,
        }}
        serverPaging={{
          totalCount,
          totalPages,
          page,
          pageSize,
          onPageChange: (p) => setPage(p),
          onPageSizeChange: (n) => {
            setPageSize(n);
            setPage(1);
          },
        }}
      />

      <CoverNoteModal
        open={!!coverRow}
        onClose={() => setCoverRow(null)}
        caseRequestId={coverRow?.caseRequestId}
        fileRefFallback={coverRow?.ref}
        flow={coverRow?.flow}
      />
    </>
  );
}
